import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import { CategoryForm } from '../components/CategoryForm';
import { apiService } from '../services/api';
import { Category, Funnel } from '../types';

export function CategoryEditorPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const defaultFunnelId = searchParams.get('funnelId') || undefined;
  const returnTo = defaultFunnelId ? `/funis?funnelId=${defaultFunnelId}` : '/categorias';
  const [category, setCategory] = useState<Category | undefined>();
  const [funnels, setFunnels] = useState<Funnel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isEdit = Boolean(id);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [funnelsData, categoryData] = await Promise.all([
          apiService.getFunnels(),
          isEdit && id ? apiService.getCategory(id) : Promise.resolve(undefined),
        ]);

        setFunnels(funnelsData);
        if (categoryData) {
          setCategory(categoryData);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Erro ao carregar categoria';
        setError(message);
        toast.error(message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, isEdit]);

  const title = useMemo(() => (isEdit ? 'Editar categoria' : 'Nova categoria'), [isEdit]);

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-2xl mx-auto mb-4 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-red-600 mb-4">Erro ao Carregar</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/categorias')}
            className="btn-primary py-3 px-6"
          >
            Voltar para categorias
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header
        title={title}
        subtitle={isEdit ? 'Atualize as informações da categoria' : 'Crie uma nova categoria para organizar seus contatos'}
        actions={(
          <button
            onClick={() => navigate(returnTo)}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
          >
            Voltar
          </button>
        )}
      />
      <CategoryForm
        category={category}
        funnels={funnels}
        variant="page"
        defaultFunnelId={defaultFunnelId}
        onSuccess={() => navigate(returnTo)}
        onCancel={() => navigate(returnTo)}
      />
    </>
  );
}
